import styled from '@emotion/styled';
import { DataExportTokenState } from 'common/types/dataExport';

const MONOSPACE = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** Each section (download tokens, webhooks) is one rounded card: header, description, guide, then the table. */
export const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.space(4)};
  border: 1px solid ${({ theme }) => theme.colors.borderGrey.toString()};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 3}px;
  background: ${({ theme }) => theme.v2.colours.gray0};
  max-width: 1100px;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space(3)};
  padding: ${({ theme }) => theme.space(3, 3, 0)};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: ${({ theme }) => theme.v2.text.variants.T1.fontSize};
  font-weight: ${({ theme }) => theme.v2.text.variants.T1.fontWeight};
  line-height: ${({ theme }) => theme.v2.text.variants.T1.lineHeight};
  color: ${({ theme }) => theme.v2.colours.gray900};
`;

export const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(0.5)};
  margin-top: ${({ theme }) => theme.space(0.5)};
  margin-bottom: ${({ theme }) => theme.space(2.5)};
  padding: ${({ theme }) => theme.space(0, 3)};
  max-width: 760px;

  /* The legacy body2 variant is semi-bold; descriptions are regular, muted text. */
  p {
    line-height: 2rem;
    font-weight: 500;
    margin: 0;
    color: ${({ theme }) => theme.v2.colours.gray500};
  }
`;

export const Code = styled.code`
  font-family: ${MONOSPACE};
  font-size: 1.2rem;
  word-break: break-all;
  color: ${({ theme }) => theme.v2.colours.gray700};
  background: ${({ theme }) => theme.v2.colours.gray50};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 1.5}px;
  padding: ${({ theme }) => theme.space(0.25, 0.75)};
`;

export const Hint = styled.div`
  padding: ${({ theme }) => theme.space(0, 3, 1.5)};
`;

/** The API endpoint with a copy button, shown under a section's description. */
export const EndpointRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space(1.5)};
  padding: ${({ theme }) => theme.space(0, 3, 2.5)};
`;

export const EndpointLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.v2.colours.gray500};
`;

export const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 1px solid ${({ theme }) => theme.v2.colours.gray100};
`;

export const Row = styled.li<{ columns: string }>`
  display: grid;
  grid-template-columns: ${({ columns }) => columns};
  gap: ${({ theme }) => theme.space(2)};
  align-items: center;
  padding: ${({ theme }) => theme.space(2, 3)};
  border-bottom: 1px solid ${({ theme }) => theme.v2.colours.gray100};

  &:last-child {
    border-bottom: none;
  }

  /* Body copy in rows is regular weight; the legacy body2 variant is semi-bold. */
  p {
    line-height: 2rem;
    font-weight: 500;
    margin: 0;
  }

  /* Narrow screens: one column per row, the header labels are dropped. */
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space(1)};

    &[data-header='true'] {
      display: none;
    }
  }

  &[data-header='true'] {
    padding: ${({ theme }) => theme.space(1.25, 3)};
    background: ${({ theme }) => theme.v2.colours.gray50};

    p {
      font-size: 1.2rem;
      line-height: 1.6rem;
      font-weight: 700;
      color: ${({ theme }) => theme.v2.colours.gray500};
    }
  }
`;

export const Cell = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
`;

/** A value's column name, read by screen readers only (the visual header row is aria-hidden). */
export const ColumnLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const TokenName = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.space(0.5)};
  color: ${({ theme }) => theme.v2.colours.gray900};

  &[data-unnamed='true'] {
    font-weight: 400;
    font-style: italic;
    color: ${({ theme }) => theme.v2.colours.gray500};
  }
`;

export const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const Badge = styled.span<{ state: DataExportTokenState }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(0.75)};
  border-radius: 999px;
  padding: ${({ theme }) => theme.space(0.5, 1.25)};
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.6rem;
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  color: ${({ theme, state }) =>
    ({ live: theme.v2.colours.green800, revoked: theme.v2.colours.gray600, expired: theme.v2.colours.orange800 }[state])};
  background: ${({ theme, state }) =>
    ({ live: theme.v2.colours.green50, revoked: theme.v2.colours.gray100, expired: theme.v2.colours.orange50 }[state])};
`;

export const StateContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.space(4, 0)};
`;

/** Placeholder rows shown while a section loads, so the card does not jump when the data arrives. */
export const SkeletonRows = styled.div`
  border-top: 1px solid ${({ theme }) => theme.v2.colours.gray100};
`;

export const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.6fr 1.2fr;
  gap: ${({ theme }) => theme.space(2)};
  align-items: center;
  padding: ${({ theme }) => theme.space(2.5, 3)};
  border-bottom: 1px solid ${({ theme }) => theme.v2.colours.gray100};

  &:last-child {
    border-bottom: none;
  }
`;

export const SkeletonBar = styled.div<{ width: string }>`
  height: 14px;
  width: ${({ width }) => width};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 1.5}px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.v2.colours.gray100} 25%,
    ${({ theme }) => theme.v2.colours.gray50} 50%,
    ${({ theme }) => theme.v2.colours.gray100} 75%
  );
  background-size: 200% 100%;
  animation: data-export-shimmer 1.4s linear infinite;

  @keyframes data-export-shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(2)};
  margin: ${({ theme }) => theme.space(0, 3, 3)};
  padding: ${({ theme }) => theme.space(2)};
  border: 1px solid ${({ theme }) => theme.v2.colours.red100};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  background: ${({ theme }) => theme.v2.colours.red50};

  p {
    margin: 0;
    color: ${({ theme }) => theme.v2.colours.red800};
  }
`;

export const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space(0.5)};
  padding: ${({ theme }) => theme.space(4, 3, 5)};
  border-top: 1px solid ${({ theme }) => theme.v2.colours.gray100};
  text-align: center;

  p {
    margin: 0;
    max-width: 360px;
    line-height: 2rem;
  }
`;

// Modals

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(2)};
  padding: ${({ theme }) => theme.space(1, 3, 3)};

  p {
    line-height: 2rem;
    font-weight: 500;
  }
`;

export const Warning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space(1.5)};
  padding: ${({ theme }) => theme.space(1.5, 2)};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  border: 1px solid ${({ theme }) => theme.v2.colours.orange200};
  background: ${({ theme }) => theme.v2.colours.orange50};
`;

export const SecretRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(1)};
`;

export const SecretField = styled.input`
  flex: 1;
  min-width: 0;
  height: 40px;
  font-family: ${MONOSPACE};
  font-size: 1.3rem;
  color: ${({ theme }) => theme.v2.colours.gray900};
  background: ${({ theme }) => theme.v2.colours.gray50};
  border: 1px solid ${({ theme }) => theme.v2.colours.gray200};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  padding: ${({ theme }) => theme.space(0, 1.5)};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.lightPurple.toString()};
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(1)};
  cursor: pointer;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.v2.colours.gray900};

  input {
    accent-color: ${({ theme }) => theme.colors.lightPurple.toString()};
    width: 16px;
    height: 16px;
    margin: 0;
  }
`;

/** The modal's footer: a divider and the buttons, running edge to edge below the body's padding. */
export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.space(1)};
  margin: ${({ theme }) => theme.space(1, -3, -3)};
  padding: ${({ theme }) => theme.space(2, 3)};
  border-top: 1px solid ${({ theme }) => theme.v2.colours.gray100};
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(0.5)};

  input {
    width: 100%;
  }
`;

export const ErrorList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.space(2)};
  color: ${({ theme }) => theme.colors.wine.toString()};
  font-size: 1.4rem;
`;

// Guides

export const Guide = styled.div`
  margin-bottom: ${({ theme }) => theme.space(2.5)};
  padding: ${({ theme }) => theme.space(0, 3)};
`;

/** The button that opens a guide: purple link text with an arrow, like the other inline links in the app. */
export const GuideLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.lightPurple.toString()};

  &::after {
    content: ' →';
  }

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.lightPurple.toString()};
    outline-offset: 2px;
  }
`;

export const GuideBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(3.5)};
  padding: ${({ theme }) => theme.space(3, 5, 5)};
  max-height: 70vh;
  overflow-y: auto;

  p {
    line-height: 2rem;
  }
`;

export const GuideList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(1.5)};
  margin: 0;
  padding-left: ${({ theme }) => theme.space(2.5)};

  &[data-plain='true'] {
    list-style: none;
    padding-left: 0;
  }
`;

export const GuideItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(0.5)};
`;

export const CodeBlockContainer = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 0;
  max-width: 100%;
  gap: ${({ theme }) => theme.space(1)};
  padding: ${({ theme }) => theme.space(1, 1, 1, 1.5)};
  background: ${({ theme }) => theme.v2.colours.gray50};
  border: 1px solid ${({ theme }) => theme.v2.colours.gray200};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
`;

export const Pre = styled.pre`
  flex: 1;
  max-height: 260px;
  min-width: 0;
  margin: 0;
  padding: ${({ theme }) => theme.space(0.75, 0)};
  overflow-x: auto;
  font-family: ${MONOSPACE};
  font-size: 1.3rem;
  line-height: 2.2rem;
  white-space: pre;
  color: ${({ theme }) => theme.v2.colours.gray900};

  /* Long curl commands wrap so they are fully visible. */
  &[data-wrap='true'] {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    overflow-x: hidden;
  }

  [data-token='comment'] {
    color: ${({ theme }) => theme.v2.colours.gray500};
    font-style: italic;
  }

  [data-token='string'] {
    color: ${({ theme }) => theme.v2.colours.green800};
  }

  [data-token='keyword'] {
    color: ${({ theme }) => theme.v2.colours.latana700};
    font-weight: 700;
  }

  [data-token='number'] {
    color: ${({ theme }) => theme.v2.colours.orange700};
  }

  [data-token='variable'] {
    color: ${({ theme }) => theme.v2.colours.cyan700};
  }

  [data-token='flag'] {
    color: ${({ theme }) => theme.v2.colours.blue700};
  }

  [data-token='command'] {
    color: ${({ theme }) => theme.v2.colours.latana800};
    font-weight: 700;
  }

  [data-token='property'] {
    color: ${({ theme }) => theme.v2.colours.indigo700};
  }
`;

export type BadgeTone = 'green' | 'orange' | 'red' | 'gray';

/** A coloured status label (webhook and delivery states). */
export const ToneBadge = styled.span<{ tone: BadgeTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(0.75)};
  border-radius: 999px;
  padding: ${({ theme }) => theme.space(0.5, 1.25)};
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.6rem;
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  color: ${({ theme, tone }) =>
    ({ green: theme.v2.colours.green800, orange: theme.v2.colours.orange800, red: theme.v2.colours.red800, gray: theme.v2.colours.gray600 }[
      tone
    ])};
  background: ${({ theme, tone }) =>
    ({ green: theme.v2.colours.green50, orange: theme.v2.colours.orange50, red: theme.v2.colours.red50, gray: theme.v2.colours.gray100 }[
      tone
    ])};
`;

export const RowActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.space(1)};
`;

/** The expanded "Recent deliveries" panel under a webhook row. */
export const DeliveriesPanel = styled.li`
  padding: ${({ theme }) => theme.space(2, 3, 3)};
  border-bottom: 1px solid ${({ theme }) => theme.v2.colours.gray100};
  background: ${({ theme }) => theme.v2.colours.gray50};

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 ${({ theme }) => theme.v2.borderRadius * 3 - 1}px ${({ theme }) => theme.v2.borderRadius * 3 - 1}px;
  }
`;

export const DeliveriesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${({ theme }) => theme.space(1)};

  th,
  td {
    padding: ${({ theme }) => theme.space(0.75, 1)};
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid ${({ theme }) => theme.v2.colours.gray100};
    overflow-wrap: anywhere;
  }

  th {
    font-weight: 700;
    font-size: 1.2rem;
    color: ${({ theme }) => theme.v2.colours.gray500};
  }
`;

export const DeliveriesScroller = styled.div`
  overflow-x: auto;
`;

/** Tutorial layout: sections with a bold heading, numbered steps, tables of codes, bullets and a note. */
export const GuideSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(1)};
`;

export const GuideHeading = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: ${({ theme }) => theme.v2.text.variants.T2.fontSize};
  font-weight: ${({ theme }) => theme.v2.text.variants.T2.fontWeight};
  line-height: ${({ theme }) => theme.v2.text.variants.T2.lineHeight};
  color: ${({ theme }) => theme.v2.colours.gray900};
`;

export const GuideNote = styled.div`
  padding: ${({ theme }) => theme.space(1.5, 2)};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  background: ${({ theme }) => theme.v2.colours.latana50};

  p {
    margin: 0;
    line-height: 2rem;
    font-weight: 500;
    color: ${({ theme }) => theme.v2.colours.latana1000};
  }
`;

export const GuideSteps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(2.5)};
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: guide-step;

  > li {
    counter-increment: guide-step;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space(0.5)};
    align-items: flex-start;
    padding-left: ${({ theme }) => theme.space(5)};
  }

  > li::before {
    content: counter(guide-step);
    position: absolute;
    left: 0;
    top: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 1.3rem;
    font-weight: 700;
    background: ${({ theme }) => theme.v2.colours.latana100};
    color: ${({ theme }) => theme.v2.colours.latana900};
  }

  p {
    margin: 0;
    font-weight: 500;
    color: ${({ theme }) => theme.v2.colours.gray500};
  }

  /* A step with a description has its title on the first line. */
  > li > p:first-of-type:not(:last-of-type) {
    font-weight: 700;
    color: ${({ theme }) => theme.v2.colours.gray900};
  }
`;

export const GuideTable = styled.div`
  border: 1px solid ${({ theme }) => theme.v2.colours.gray100};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  overflow: hidden;
`;

export const GuideTableRow = styled.div`
  display: grid;
  grid-template-columns: minmax(72px, auto) 1fr;
  gap: ${({ theme }) => theme.space(1.5)};
  align-items: baseline;
  padding: ${({ theme }) => theme.space(1.25, 1.75)};
  border-top: 1px solid ${({ theme }) => theme.v2.colours.gray100};

  &:first-of-type {
    border-top: none;
  }

  p {
    margin: 0;
    font-weight: 500;
    color: ${({ theme }) => theme.v2.colours.gray900};
  }
`;

export const GuideBullets = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(0.5)};
  margin: 0;
  padding-left: ${({ theme }) => theme.space(2.25)};

  p {
    margin: 0;
    font-weight: 500;
    color: ${({ theme }) => theme.v2.colours.gray500};
  }
`;

/** The page-level "no access" state: one centred card with a round icon, a title, a line of text and one action. */
export const NoAccessCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space(0.5)};
  max-width: 1100px;
  padding: ${({ theme }) => theme.space(8, 3)};
  border: 1px solid ${({ theme }) => theme.colors.borderGrey.toString()};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 3}px;
  text-align: center;

  p {
    margin: 0;
    max-width: 420px;
    line-height: 2rem;
    font-weight: 500;
  }

  button {
    margin-top: ${({ theme }) => theme.space(2)};
  }
`;

export const NoAccessIcon = styled.div`
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  margin-bottom: ${({ theme }) => theme.space(1.5)};
  border-radius: 50%;
  background: ${({ theme }) => theme.v2.colours.latana50};
`;

export const NoAccessTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: ${({ theme }) => theme.v2.text.variants.T1.fontSize};
  font-weight: ${({ theme }) => theme.v2.text.variants.T1.fontWeight};
  line-height: ${({ theme }) => theme.v2.text.variants.T1.lineHeight};
  color: ${({ theme }) => theme.v2.colours.gray900};
`;

/** Second, muted line of a table cell (who created it, the time of a date). */
export const MutedLine = styled.div`
  font-size: 1.2rem;
  line-height: 1.6rem;
  color: ${({ theme }) => theme.v2.colours.gray500};

  &[data-tone='orange'] {
    color: ${({ theme }) => theme.v2.colours.orange700};
  }

  &[data-tone='red'] {
    color: ${({ theme }) => theme.v2.colours.red700};
  }
`;

export const WebhookUrl = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.v2.colours.gray900};
  overflow-wrap: anywhere;
`;

/** The "⋯" row menu. */
export const MenuWrapper = styled.div`
  position: relative;
`;

export const MenuButton = styled.button`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  background: transparent;
  cursor: pointer;

  &:hover,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.v2.colours.gray50};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.lightPurple.toString()};
  }
`;

export const MenuList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 10;
  min-width: 160px;
  padding: ${({ theme }) => theme.space(0.5)};
  border: 1px solid ${({ theme }) => theme.v2.colours.gray200};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  background: ${({ theme }) => theme.v2.colours.gray0};
  box-shadow: 0 8px 24px rgba(20, 20, 20, 0.12);
`;

export const MenuItem = styled.button`
  display: block;
  width: 100%;
  padding: ${({ theme }) => theme.space(1, 1.5)};
  border: none;
  border-radius: ${({ theme }) => theme.v2.borderRadius}px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.v2.colours.red700};

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.v2.colours.red50};
    outline: none;
  }
`;

/** The round tinted icon of an empty state. */
export const StateIcon = styled.div`
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin-bottom: ${({ theme }) => theme.space(1)};
  border-radius: 50%;
  background: ${({ theme }) => theme.v2.colours.latana50};
`;

// Form fields of the modals
export const FieldLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space(0.75)};
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.v2.colours.gray900};
`;

export const TextField = styled.input<{ mono?: boolean }>`
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.space(0, 1.5)};
  border: 1px solid ${({ theme }) => theme.v2.colours.gray200};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  background: ${({ theme }) => theme.v2.colours.gray0};
  font-family: ${({ mono }) => (mono ? MONOSPACE : 'inherit')};
  font-size: ${({ mono }) => (mono ? '1.3rem' : '1.4rem')};
  font-weight: 500;
  color: ${({ theme }) => theme.v2.colours.gray900};

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.lightPurple.toString()};
    border-color: ${({ theme }) => theme.colors.lightPurple.toString()};
  }

  &[aria-invalid='true'] {
    border-color: ${({ theme }) => theme.v2.colours.red700};
  }
`;

export const FieldHint = styled.p`
  margin: ${({ theme }) => theme.space(0.75, 0, 0)};
  font-size: 1.2rem;
  font-weight: 500;
  color: ${({ theme }) => theme.v2.colours.gray500};
`;

export const FieldError = styled.p`
  margin: ${({ theme }) => theme.space(0.75, 0, 0)};
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.v2.colours.red700};
`;

/** A request failure inside a modal: a red banner, one paragraph per backend message. */
export const ErrorBanner = styled.div`
  padding: ${({ theme }) => theme.space(1.5, 1.75)};
  border: 1px solid ${({ theme }) => theme.v2.colours.red100};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  background: ${({ theme }) => theme.v2.colours.red50};

  p {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 600;
    color: ${({ theme }) => theme.v2.colours.red800};
  }
`;

/** Bottom padding keeps scrolled content clear of the selected tab. */
export const GuideTabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space(0.5)};
  padding: ${({ theme }) => theme.space(2, 5, 1.5)};
`;

export const GuideTab = styled.button`
  height: 32px;
  padding: ${({ theme }) => theme.space(0, 1.5)};
  border: none;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.v2.colours.gray600};

  &[aria-selected='true'] {
    background: ${({ theme }) => theme.v2.colours.latana50};
    color: ${({ theme }) => theme.v2.colours.latana900};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.lightPurple.toString()};
  }
`;

/** Pushes a guide link to the right end of a row. */
export const GuideSlot = styled.div`
  margin-left: auto;
`;

/** A section's guide link on its own row, at the right like the one in the endpoint row (webhooks have no endpoint). */
export const GuideRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.space(0, 3, 2.5)};
`;

/** The endpoint URL, as tall as the button next to it. */
export const EndpointValue = styled.code`
  display: inline-flex;
  align-items: center;
  height: 40px;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.space(0, 1.5)};
  border: 1px solid ${({ theme }) => theme.v2.colours.gray100};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  background: ${({ theme }) => theme.v2.colours.gray50};
  font-family: ${MONOSPACE};
  font-size: 1.3rem;
  color: ${({ theme }) => theme.v2.colours.gray700};
  word-break: break-all;
`;

/** Footer of a paginated table: the range on the left, previous and next on the right. */
export const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.space(2)};
  padding: ${({ theme }) => theme.space(1.5, 3)};
  border-top: 1px solid ${({ theme }) => theme.v2.colours.gray100};

  p {
    margin: 0;
    font-weight: 500;
    color: ${({ theme }) => theme.v2.colours.gray500};
  }
`;

/** Previous, the page numbers and Next as one bordered group, like Bootstrap's pagination but in the app's colours. */
export const PageGroup = styled.nav`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.v2.colours.gray200};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
  overflow: hidden;
`;

export const PageButton = styled.button`
  min-width: 40px;
  height: 36px;
  padding: ${({ theme }) => theme.space(0, 1.5)};
  border: none;
  border-left: 1px solid ${({ theme }) => theme.v2.colours.gray200};
  background: ${({ theme }) => theme.v2.colours.gray0};
  cursor: pointer;
  font-family: ${({ theme }) => theme.v2.text.fontFamily};
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.lightPurple.toString()};

  &:first-of-type {
    border-left: none;
  }

  &:hover:not(:disabled):not([aria-current='page']) {
    background: ${({ theme }) => theme.v2.colours.latana50};
  }

  &[aria-current='page'] {
    background: ${({ theme }) => theme.colors.lightPurple.toString()};
    color: ${({ theme }) => theme.colors.white.toString()};
    cursor: default;
  }

  &:disabled:not([aria-current='page']) {
    color: ${({ theme }) => theme.v2.colours.gray400};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.lightPurple.toString()};
    outline-offset: -2px;
  }
`;
